import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Deployment, DeploymentJsonBody } from "../models/deployment";
import {
  BehaviorSubject,
  Observable,
  catchError,
  lastValueFrom,
  map,
  of,
  tap,
} from "rxjs";
import { UserService } from "./user.service";
import { AuthenticationService } from "./authentication.service";
import { UserRolePipe } from "../pipes/user-role.pipe";
import { EnvironmentService } from "./environment.service";
import { HeaderService } from "./header.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { UserRoleNames } from "../models/users";
import { CustomData } from "../models/customData";
import { CloneRepositoryInfo, Repository } from "../models/repository";
import { GiteaRepository } from "../models/gitea";
import { HeadNodeNameIp } from "../models/HeadNodeNameIp";

@Injectable({
  providedIn: "root",
})
export class DeploymentService {
  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private env: EnvironmentService,
    private headerService: HeaderService,
    private snackBar: MatSnackBar
  ) {}

  private currentUserAccess = new BehaviorSubject<
    { depName: string; role: string }[]
  >([]);
  public CurrentUserAccess = this.currentUserAccess.asObservable();

  dashboardReportRedirect: string | null = null;
  dashboardReportRedirectDep: string | null = null;

  newDepType: string | null = null;

  updateCurrentUserAccess(depName: string, role: string) {
    let tempArr = this.currentUserAccess.value;
    if (depName) {
      let idx = this.currentUserAccess.value.findIndex(
        (val) => val.depName == depName
      );
      if (idx == -1) {
        tempArr.push({ depName: depName, role: role });
        this.currentUserAccess.next(tempArr);
      }
    }
  }

  emptyUserAccess() {
    this.currentUserAccess.next([]);
  }

  //////////////////////////////////////////////////////////////

  private allProfileInfoes = new BehaviorSubject<Deployment[] | null>(null);
  public AllProfileInfoes = this.allProfileInfoes.asObservable();
  public allProfileHeadnodeNamesIps = new BehaviorSubject<any[]>([]);
  public AllProfileHeadnodeNamesIps = this.allProfileHeadnodeNamesIps.asObservable();
  
  updateAllProfileInfoes(deployments: Deployment[]) {
    if (deployments != this.allProfileInfoes.value) {
      this.allProfileInfoes.next(deployments);
    }
  }

  getProfileInfoesFromSubject() {
    return this.allProfileInfoes.value;
  }

  isDeploymentPresent(depName: string) {
    if (!this.allProfileInfoes.value) return false;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment != null;
  }

  getRescaleFlagOfDeployment(depName: string): boolean {
    if (!this.allProfileInfoes.value) return null;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    if (deployment) {
      return deployment.autoRescaleFlag;
    }
    return null;
  }

  getDeploymentNamesFromSubject() {
    if (!this.allProfileInfoes.value) return [];
    let deploymentNames = this.allProfileInfoes?.value?.map((dep) => dep.name);

    return deploymentNames;
  }

  getProfileInfoFromSubject(depName: String) {
    if (this.allProfileInfoes.value == null) {
      this.getAllProfileInfoes().subscribe();
    }
    return this.allProfileInfoes?.value?.find((dep) => dep.name == depName);
  }

  get isUserJobOperator(): boolean {
    return this.currentUserAccess?.value.every(
      (d) => d.role == UserRoleNames.JobOperator
    );
  }

  isShowWorkstoreUploadEnabled(depName) {
    if (!this.allProfileInfoes.value) return false;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment.showWorkstoreUpload;
  }

  isShowWorkstoreDownloadEnabled(depName) {
    if (!this.allProfileInfoes.value) return true;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment.showWorkstoreDownload;
  }

  isUseLowerCaseForNodesEnabled(depName: string) {
    if (!this.allProfileInfoes.value) return false;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment.useLowerCaseForNodes;
  }

  isRouteViaSynchpcEnabled(depName) {
    if (!this.allProfileInfoes.value) return false;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment?.routeViaSynchpc;
  }

  isLocalStoreEnabled(depName) {
    if (!this.allProfileInfoes.value) return false;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment?.isLocalStore;
  }

  getUrlForUtilityService(
    depName: string,
    ip: string,
    port: string,
    type: number,
    routeViaSynchpc?: boolean,
    downloadPath?: string
  ): string {
    let url = "";
    let prof: Deployment = this.allProfileInfoes?.value?.find(
      (dep) => dep.name == depName
    );
    if (!ip) {
      this.snackBar.open("Fetching data, please wait.", "Ok", {
        duration: 5000,
      });
      this.getAllDeploymentsForUser().subscribe();
      return;
    }

    /*if (routeViaSynchpc == undefined) {
      if (!prof) {
        this.snackBar.open("Fetching data, please wait.", "Ok", {
          duration: 5000
        })
        this.getAllDeploymentsForUser().subscribe();
        return;
      }
      else {
        routeViaSynchpc = prof.routeViaSynchpc;
      }
    }*/

    if (routeViaSynchpc) {
      url = `${this.env.envVariables.httpProtocol}${
        this.env.envVariables.webHost.split("/")[0]
      }/service/${ip}:${port}/`;
      if (type == 4 && this.env.envVariables.skipRouteViaSynchpcForTerminal) {
        url = `${this.env.envVariables.httpProtocol}${ip}:${port}/`;
      } else {
        url = `${this.env.envVariables.httpProtocol}${
          this.env.envVariables.webHost.split("/")[0]
        }/service/${ip}:${port}/`;
      }
    } else {
      url = `${this.env.envVariables.httpProtocol}${ip}:${port}/`;
    }
    // 0 = authorize, 1 = upload, 2 = download
    if (type == 0) {
      url += "authorize";
    } else if (type == 1) {
      url += "upload";
    } else if (type == 2) {
      // url = url.split("s:").join(":");
      url += downloadPath;
      // url = url.replace("service/","");
    }
    return url;
  }

  isCheckUtilityServiceEnabled(depName) {
    if (!this.allProfileInfoes.value) return false;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment?.isCheckUtilityServiceEnabled;
  }

  getStorageAccNameForDeployment(depName: string) {
    if (!this.allProfileInfoes.value) return null;
    let deployment = this.allProfileInfoes.value.find(
      (dep) => dep.name == depName
    );
    return deployment.storageAccountName.split(",");
  }

  ///////////////////////////////////////////////////////////////
  //apis

  getAllCloudProviders() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/CloudProviders";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getCloudProviderDetails(cloudValue: number, cloudName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/CloudProviders/Details";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CloudValue", cloudValue.toString())
      .set("CloudName", cloudName);
    return this.http.get(baseUrl, { headers: headers, responseType: "text" });
  }

  createDeployment(
    cloudValue: number,
    cloudName: string,
    depJsonBody: DeploymentJsonBody,
    skipCloudDep: boolean
  ) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "ProfileInfoes/CloudProviders/CreateDeployment";

      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json")
        .set("Cache-Control", "no-cache")
        .set("SkipCloudDep", skipCloudDep.toString())
        .set("CloudValue", cloudValue.toString())
        .set("CloudName", cloudName);

      let options = { headers: headers };

      return this.http.post<any>(baseUrl, JSON.stringify(depJsonBody), options);
    } catch (e) {}
  }

  getAllProfileInfoes(): Observable<any> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers }).pipe(
      tap((data: Deployment[] | null) => {
        this.updateAllProfileInfoes(JSON.parse(JSON.stringify(data)));
        this.headerService.getQuickAccessControlSummary();
      }),
      catchError((err) => {
        return of(null);
      })
    );
  }

  getAllDeploymentsForUser() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers }).pipe(
      tap((data: Deployment[] | null) => {
        this.updateAllProfileInfoes(JSON.parse(JSON.stringify(data)));
        this.headerService.getQuickAccessControlSummary();
      }),
      catchError((err) => {
        return err;
      })
    );
  }

  getDeployment(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/" +
      depName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("userDeploymentName", depName)
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getDeploymentInfoForJobs(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/" +
      depName +
      "/GetDeploymentInfoForJobs";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("userDeploymentName", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getCsvGraphData(depName: string, period: string = null) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/getCsvGraphDataForDeployment/Period=" +
      period;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  setPropertyForDeployment(depName: string, paramName: string, value: number) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes" +
      depName +
      "/" +
      paramName +
      "/" +
      value;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    const body = { title: "Angular PUT Request" };
    this.http.put(baseUrl, body, { headers: headers }).subscribe({
      next: (data) => {},
      error: (error) => {},
    });
  }

  getDeploymentStatus(depName: string, depID: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/CloudProviders/Status/id=" +
      depID;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  deleteDeployment(depName: string, skipCloud: boolean = false) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/delete";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("DeploymentName", depName)
      .set("SkipCloud", skipCloud.toString());
    return this.http.post(baseUrl, null, { headers: headers });
  }

  checkUserDepAccess() {
    let userName = this.authenticationService.currentUserValue?.username;
    if (!userName) return;
    this.userService.getUserProfileInfoes(userName).subscribe({
      next: (data: Deployment[]) => {
        if (!data) return;
        let deployments = JSON.parse(JSON.stringify(data));
        for (const dep of deployments) {
          let pipe = new UserRolePipe();
          let role = pipe.transform(dep.userRole);
          this.updateCurrentUserAccess(dep.deploymentName, role);
        }
      },
      error: (err) => {},
    });
  }

  getStorageNameForDeployment(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  editNodeGroup(depName: string, nodeGroup: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/editNodeGroup/" +
      nodeGroup;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.put(baseUrl, nodeGroup, { headers: headers });
  }

  updateDeploymentAdminCredentials(depName: string, credObj: any) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/UpdateDeploymentAdminCredentials";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put(baseUrl, credObj, { headers: headers });
  }

  getDeploymentNames() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/GetDepNamesAll";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  checkIsVdi(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/CheckIfDeploymentVDI";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  checkIsOnpremiseVdi(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/CheckIfDeploymentOnpremiseVDI";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getAdminUsernameForDeployment(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/GetAdminUsernameForDeployment";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  //! Custom data comoponent needed apis are here

  getCustomData() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "SyncEntities/CustomData";
    let headers = new HttpHeaders();

    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.get<CustomData[] | CustomData>(baseUrl, {
      headers: headers,
    });
  }

  //! End of custom data component apis

  getNodeGroupsForDeployment(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/nodegroups";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  addDeploymentRdpFile(depName: string, rdpFile: File, fileType: string) {
    try {
      const formData = new FormData();
      formData.append("File", rdpFile);

      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "ProfileInfoes/Deployment/" +
        depName +
        "/UploadDeploymentRdpFile";
      let headers = new HttpHeaders();
      headers = headers
        .set("Cache-Control", "no-cache")
        .set("Access-Control-Allow-Origin", "*")
        .set(
          "Access-Control-Allow-Headers",
          "Origin, X-Requested-With, Content-Type, Accept"
        )
        .set("FileType", fileType);

      const upload$ = this.http.post<any>(baseUrl, formData, {
        headers: headers,
        reportProgress: true,
        observe: "events",
      });

      return upload$;
    } catch (Error) {}
  }

  isHeadNodeActive(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/" +
      depName +
      "/GetHeadNodeState";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  updateDeploymentName(depName: string, newDepName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/Deployment/ChangeDeploymentName/" +
      newDepName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Deployment", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  updatePartition(
    depName: string,
    schedular: string,
    setOtherProperties: string,
    updateNodeGroupValues,
    action: string,
    isVdi: boolean = false
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ProfileInfoes/Deployment/${depName}/UpdatePartition`;
    let headers = new HttpHeaders();

    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("SetOtherProperties", setOtherProperties)
      .set("Action", action)
      .set("IsVdi", isVdi.toString());

    return this.http.post(baseUrl, updateNodeGroupValues, { headers: headers });
  }

  getAllCloudProvidersValueText() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/CloudProviders/ValueText";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  powerOffGracefully(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ProfileInfoes/Deployment/${depName}/PowerOffGracefully`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("FromUI", "true");
    return this.http.post(baseUrl, { name: "powerOff" }, { headers: headers });
  }

  powerOnGracefully(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ProfileInfoes/Deployment/${depName}/PowerOnGracefully`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("FromUI", "true");
    return this.http.post(baseUrl, { name: "powerOn" }, { headers: headers });
  }

  updateCluster(depName: string, updateClusterValues) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ProfileInfoes/Deployment/${depName}/UpdateCluster`;
    let headers = new HttpHeaders();

    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.post(baseUrl, updateClusterValues, { headers: headers });
  }

  getCloudJsonValues(
    cloudValue: number,
    cloudName: string,
    depName: string,
    partitionName: string = ""
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ProfileInfoes/CloudProvider/${cloudName}/CloudJsonValues`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CloudValue", cloudValue.toString())
      .set("CloudName", cloudName)
      .set("PartitionName", partitionName)
      .set("DeploymentName", depName);
    return this.http.get(baseUrl, { headers: headers, responseType: "text" });
  }

  createUpdatePartition(
    cloudValue: number,
    cloudName: string,
    depJsonBody: DeploymentJsonBody,
    action: string,
    depName: string,
    skipCloud: boolean = false
  ) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "ProfileInfoes/CreateUpdatePartition";

      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json")
        .set("Cache-Control", "no-cache")
        .set("CloudValue", cloudValue.toString())
        .set("CloudName", cloudName)
        .set("DeploymentName", depName)
        .set("Action", action)
        .set("SkipCloud", skipCloud.toString());

      let options = { headers: headers };

      return this.http.post<any>(baseUrl, JSON.stringify(depJsonBody), options);
    } catch (e) {}
  }

  getJobinfoDownloadTemplates(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ProfileInfoes/GetJobInfoDownoadTemplate`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("DeploymentName", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  adddeleteJobInfoDownloadTemplate(
    depName: string,
    action: string,
    templateDTO
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ProfileInfoes/JobInfoDownoadTemplateAction`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("DeploymentName", depName)
      .set("Action", action);
    return this.http.put(baseUrl, templateDTO, { headers: headers });
  }

  getCloudProviderDetailsForStaticDep(cloudValue: number, cloudName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/CloudProviders/Details/StaticDeploymnetForm";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CloudValue", cloudValue.toString())
      .set("CloudName", cloudName);
    return this.http.get(baseUrl, { headers: headers, responseType: "text" });
  }

  getHostsAndVmsFromKvm(friendlyName: string, fromDep: boolean = false) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/GetHostsAndMachines";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("FriendlyName", friendlyName)
      .set("UsingDepName", fromDep.toString());
    return this.http.get(baseUrl, { headers: headers });
  }

  GetLocalRepos() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/GetLocalRepos";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  public initializeHeadnodeData(): void {
    this.getHeadNodeNameIpsOfAllDeployment().subscribe({
        next: (data) => {
        },
        error: (err) => {
            console.error('Failed to initialize headnode data:', err);
        }
    });
  }

  getHeadNodeNameIpsOfAllDeployment(): Observable<HeadNodeNameIp[]> {
        let baseUrl =
            this.env.envVariables.httpProtocol +
            this.env.envVariables.webHost +
            "ProfileInfoes/GetHeadnodeNameIpOfAllDeployment";

        let headers = new HttpHeaders();
        headers = headers
            .set("Content-Type", "application/json; charset=utf-8")
            .set("Cache-Control", "no-cache");

        return this.http.post<HeadNodeNameIp[]>(baseUrl, {}, { headers: headers }).pipe(
            tap((data: HeadNodeNameIp[]) => {
                this.allProfileHeadnodeNamesIps.next(data);
            }),
            catchError((err) => {
                console.error('Error fetching headnode IPs:', err);
                return of([]);
            })
        );
    }

    getHeadNodeIpForDeployment(depName: string): string | null {
        if (this.allProfileHeadnodeNamesIps.value.length === 0) {
            this.initializeHeadnodeData();
        }
        const headnodes = this.allProfileHeadnodeNamesIps.value;
        const headnode = headnodes.find(h => h.deploymentName === depName);
        return headnode?.headnodeIp || null;
    }

    getHeadNodeNameForDeployment(depName: string): string | null {
        if (this.allProfileHeadnodeNamesIps.value.length === 0) {
            this.initializeHeadnodeData();
        }
        const headnodes = this.allProfileHeadnodeNamesIps.value;
        const headnode = headnodes.find(h => h.deploymentName === depName);
        return headnode?.headnodeName || null;
    }
}
